import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { LanguageService } from '@core/i18n/language.service';

export type ModalText = string | (() => string);

export enum ModalType {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CONFIRM = 'confirm',
  FORM = 'form',
  PICTURE_UPLOAD = 'picture-upload',
  PASSWORD_CHANGE = 'password-change',
  CUSTOM = 'custom',
  TABLE = 'table',
  SHARE = 'SHARE'
}

export interface ModalButton {
  text: ModalText;
  value: any;
  primary?: boolean;
  close?: boolean;
  action?: () => void;
  class?: string;
}

export interface ModalOptions {
  id?: string;
  title?: ModalText;
  message?: ModalText;
  type?: ModalType;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closable?: boolean;
  buttons?: ModalButton[];
  data?: any;
  component?: any;
  formFields?: ModalFormField[];
  customClass?: string;
  backdropClose?: boolean;
  zIndex?: number;
}

export interface ModalFormField {
  name: string;
  label?: ModalText;
  type: 'text' | 'textarea' | 'number' | 'select' | 'multiselect' | 'checkboxlist' | 'checkbox' | 'radio' | 'date' | 'password' | 'time' | 'email';
  value?: any;
  /** Only for type 'multiselect': false renders/stores it as a single value instead of an array. Defaults to true (unchanged behavior). */
  multiple?: boolean;
  options?: {
    label: string;
    value: any;
    children?: { fieldName: string; options: { label: string; value: any }[] };
  }[];
  placeholder?: ModalText;
  required?: boolean;
  validators?: any[];
  errorMessage?: string;
  class?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  allowDecimals?: boolean;
  hidden?: boolean;
  onChange?: (value: any, form: any, field: ModalFormField) => void;

  showWhen?: {
    field: string;
    value: any;
    operator?: 'eq' | 'neq' | 'contains' | 'in';
  }[];
}

export interface ModalResult {
  value: any;
  data?: any;
}

export interface ModalRef {
  id: string;
  close: (result?: any) => void;
  update: (options: Partial<ModalOptions>) => void;
}

export interface FieldOptionsUpdate {
  modalId: string;
  fieldName: string;
  options: { label: string; value: any; children?: { fieldName: string; options: { label: string; value: any }[] } }[];
}

export interface ModalState {
  id: string;
  options: ModalOptions;
  resultSubject: Subject<ModalResult>;
  visible: boolean;
}

export interface PictureUploadOptions {
  aspectRatio?: 'free' | 'square' | '4:3' | '16:9';
  cropControls?: boolean;
  rotateControls?: boolean;
  zoomControls?: boolean;
  resetButton?: boolean;
  cropSizeLimit?: {
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
  };
  fileTypes?: string;
  maxFileSize?: number;
  title?: ModalText;
  message?: ModalText;
  buttonLabels?: {
    save?: string;
    cancel?: string;
    reset?: string;
  };
}

export interface ImagePreviewOptions {
  title?: ModalText;
  imageUrl: string;
  showDownload?: boolean;
  downloadFilename?: string;
  buttons?: ModalButton[];
}

const ModalStore = signalStore(
  { providedIn: 'root' },
  withState({ modals: [] as ModalState[] }),
  withMethods((store) => ({
    setModals(modals: ModalState[]): void {
      patchState(store, { modals });
    },
  }))
);

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private readonly modalStore = inject(ModalStore);

  private modalsById = new Map<string, ModalState>();
  private idCounter = 0;

  private lastOpenTime = 0;
  private readonly DEBOUNCE_TIME = 200;

  private pendingModals = new Set<string>();

  public readonly modals = this.modalStore.modals;

  private fieldOptionsUpdatesSubject = new Subject<FieldOptionsUpdate>();
  public fieldOptionsUpdates$ = this.fieldOptionsUpdatesSubject.asObservable();

  private translateData: any = {};

  constructor(private languageService: LanguageService) {
    this.languageService.getSharedDictionary('modal').subscribe(dict => {
      this.translateData = dict;
    });
  }

  private t(key: string, fallback: string): () => string {
    return () => this.translateData?.[key] || fallback;
  }

  /**
   * Updates the options of a specific form field in an open modal.
   * This triggers the modal component to re-render the field options (e.g., checkboxlist labels).
   * @param modalId The ID of the modal
   * @param fieldName The name of the form field to update
   * @param options The new options array
   */
  updateFieldOptions(modalId: string, fieldName: string, options: { label: string; value: any; children?: { fieldName: string; options: { label: string; value: any }[] } }[]): void {
    const modal = this.modalsById.get(modalId);
    if (modal && modal.options.formFields) {
      const field = modal.options.formFields.find(f => f.name === fieldName);
      if (field) {
        field.options = options;
      }
    }
    this.fieldOptionsUpdatesSubject.next({ modalId, fieldName, options });
  }

  table(columns: any[], rows: any[], title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title: title || this.t('table', 'Table'),
      type: ModalType.TABLE,
      size: options.size || 'xl',
      data: {
        columns,
        rows
      },
      buttons: [
        { text: this.t('close', 'Close'), value: true, primary: true, close: true }
      ],
      customClass: 'modal-table',
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  private getNextId(): string {
    return `modal-${++this.idCounter}`;
  }

  private getActiveModals(): ModalState[] {
    return Array.from(this.modalsById.values())
      .filter(modal => modal.visible)
      .sort((a, b) => {
        const zIndexA = a.options.zIndex || 1000;
        const zIndexB = b.options.zIndex || 1000;
        return zIndexA - zIndexB;
      });
  }

  private updateModals(): void {
    this.modalStore.setModals(this.getActiveModals());
  }

  private resolveModalText(text: ModalText | undefined): string {
    if (typeof text === 'function') return text() || '';
    return text || '';
  }

  private createModalSignature(options: ModalOptions): string {
    let dataSignature = '';
    try {
      dataSignature = JSON.stringify(options.data || {});
    } catch {
      dataSignature = Object.keys(options.data || {}).join(',');
    }
    const title = this.resolveModalText(options.title);
    const message = this.resolveModalText(options.message);
    return `${options.type || 'default'}-${title}-${message}-${dataSignature}`;
  }

  private isDuplicateModal(signature: string): boolean {
    return this.pendingModals.has(signature) ||
      Array.from(this.modalsById.values()).some(modal =>
        this.createModalSignature(modal.options) === signature && modal.visible
      );
  }

  open(options: ModalOptions): ModalRef {
    const now = Date.now();

    if (now - this.lastOpenTime < this.DEBOUNCE_TIME) {
      console.warn('Modal open call debounced - too rapid successive calls');
      return {
        id: 'debounced',
        close: () => { },
        update: () => { }
      };
    }

    const signature = this.createModalSignature(options);
    if (this.isDuplicateModal(signature)) {
      console.warn('Duplicate modal detected, ignoring', signature);
      const existingModal = Array.from(this.modalsById.values()).find(modal =>
        this.createModalSignature(modal.options) === signature && modal.visible
      );
      if (existingModal) {
        return {
          id: existingModal.id,
          close: (result?: any) => this.close(existingModal.id, result),
          update: (updatedOptions: Partial<ModalOptions>) => this.update(existingModal.id, updatedOptions)
        };
      }
      return {
        id: 'duplicate',
        close: () => { },
        update: () => { }
      };
    }

    this.lastOpenTime = now;
    this.pendingModals.add(signature);

    const id = options.id || this.getNextId();
    const resultSubject = new Subject<ModalResult>();

    const modalState: ModalState = {
      id,
      options: {
        ...options,
        closable: options.closable !== undefined ? options.closable : true,
        backdropClose: options.backdropClose !== undefined ? options.backdropClose : true,
        size: options.size || 'md',
        zIndex: options.zIndex || 1000 + this.modalsById.size
      },
      resultSubject,
      visible: true
    };

    this.modalsById.set(id, modalState);
    this.updateModals();

    setTimeout(() => {
      this.pendingModals.delete(signature);
    }, this.DEBOUNCE_TIME);

    return {
      id,
      close: (result?: any) => this.close(id, result),
      update: (updatedOptions: Partial<ModalOptions>) => this.update(id, updatedOptions)
    };
  }

  close(id: string, result?: any): void {
    const modal = this.modalsById.get(id);
    if (modal) {
      modal.visible = false;
      this.updateModals();

      modal.resultSubject.next({ value: result, data: modal.options.data });
      modal.resultSubject.complete();

      const signature = this.createModalSignature(modal.options);
      this.pendingModals.delete(signature);

      setTimeout(() => {
        this.modalsById.delete(id);
      }, 300);
    }
  }

  update(id: string, options: Partial<ModalOptions>): void {
    const modal = this.modalsById.get(id);
    if (modal) {
      modal.options = { ...modal.options, ...options };
      this.updateModals();
    }
  }

  closeAll(): void {
    this.modalsById.forEach((modal, id) => {
      this.close(id);
    });
    this.pendingModals.clear();
  }

  info(message: ModalText, title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title: title || this.t('information', 'Information'),
      message,
      type: ModalType.INFO,
      buttons: [
        { text: this.t('ok', 'OK'), value: true, primary: true, close: true }
      ],
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  warning(message: ModalText, title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title: title || this.t('warning', 'Warning'),
      message,
      type: ModalType.WARNING,
      buttons: [
        { text: this.t('ok', 'OK'), value: true, primary: true, close: true }
      ],
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  error(message: ModalText, title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title: title || this.t('error', 'Error'),
      message,
      type: ModalType.ERROR,
      buttons: [
        { text: this.t('ok', 'OK'), value: true, primary: true, close: true }
      ],
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  confirm(message: ModalText, title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title: title || this.t('confirm', 'Confirm'),
      message,
      type: ModalType.CONFIRM,
      buttons: [
        { text: this.t('cancel', 'Cancel'), value: false, close: true },
        { text: this.t('ok', 'OK'), value: true, primary: true, close: true }
      ],
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  form(formFields: ModalFormField[], title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title: title || this.t('form', 'Form'),
      type: ModalType.FORM,
      formFields,
      buttons: [
        { text: this.t('cancel', 'Cancel'), value: null, close: true },
        { text: this.t('submit', 'Submit'), value: true, primary: true, close: true }
      ],
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  custom(component: any, data?: any, title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title,
      type: ModalType.CUSTOM,
      component,
      data,
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  pictureUpload(options: PictureUploadOptions = {}): Observable<ModalResult> {
    const cancelLabel = options.buttonLabels?.cancel;
    const saveLabel = options.buttonLabels?.save;
    const modalRef = this.open({
      title: options.title || this.t('upload_picture', 'Upload Picture'),
      message: options.message,
      type: ModalType.PICTURE_UPLOAD,
      size: 'md',
      buttons: [
        {
          text: cancelLabel ? cancelLabel : this.t('cancel', 'Cancel'),
          value: null,
          close: true
        },
        {
          text: saveLabel ? saveLabel : this.t('save', 'Save'),
          value: true,
          primary: true,
          close: true
        }
      ],
      data: {
        aspectRatio: options.aspectRatio || 'free',
        cropControls: options.cropControls !== undefined ? options.cropControls : true,
        rotateControls: options.rotateControls !== undefined ? options.rotateControls : true,
        zoomControls: options.zoomControls !== undefined ? options.zoomControls : true,
        resetButton: options.resetButton !== undefined ? options.resetButton : true,
        cropSizeLimit: options.cropSizeLimit || {},
        fileTypes: options.fileTypes || 'image/*',
        maxFileSize: options.maxFileSize || 20 * 1024 * 1024,
        zoom: 1,
        rotation: 0
      },
      customClass: 'modal-picture-upload'
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  passwordChange(isAdmin: boolean = false, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const formFields: ModalFormField[] = isAdmin ?
      [
        {
          name: 'newPassword',
          label: this.t('new_password', 'New Password'),
          type: 'password',
          required: true
        },
        {
          name: 'confirmPassword',
          label: this.t('confirm_password', 'Confirm Password'),
          type: 'password',
          required: true
        }
      ] :
      [
        {
          name: 'currentPassword',
          label: this.t('current_password', 'Current Password'),
          type: 'password',
          required: true
        },
        {
          name: 'newPassword',
          label: this.t('new_password', 'New Password'),
          type: 'password',
          required: true
        },
        {
          name: 'confirmPassword',
          label: this.t('confirm_password', 'Confirm Password'),
          type: 'password',
          required: true
        }
      ];

    const modalRef = this.open({
      title: this.t('change_password', 'Change Password'),
      type: ModalType.PASSWORD_CHANGE,
      formFields,
      size: 'md',
      buttons: [
        { text: this.t('cancel', 'Cancel'), value: false, close: true },
        { text: this.t('change_password', 'Change Password'), value: true, primary: true, close: true }
      ],
      data: {
        isAdmin,
        password: '',
        showRequirements: false
      },
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  image(options: ImagePreviewOptions): Observable<ModalResult> {
    const defaultButtons = [
      { text: this.t('close', 'Close'), value: true, primary: true, close: true }
    ];

    const modalRef = this.open({
      title: options.title || 'Image Preview',
      type: ModalType.CUSTOM,
      size: 'lg',
      data: {
        imageUrl: options.imageUrl,
        showDownload: options.showDownload,
        downloadFilename: options.downloadFilename,
        isImagePreview: true
      },
      buttons: options.buttons || defaultButtons,
      customClass: 'modal-image-preview'
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

  share(link: string, title?: ModalText, options: Partial<ModalOptions> = {}): Observable<ModalResult> {
    const modalRef = this.open({
      title: title || this.t('share', 'Share'),
      message: this.t('share_message', 'Choose how you want to share this link:'),
      type: ModalType.SHARE,
      size: 'sm',
      data: {
        link
      },
      buttons: [
        { text: this.t('close', 'Close'), value: false, close: true, primary: true }
      ],
      ...options
    });

    const modal = this.modalsById.get(modalRef.id);
    if (!modal) {
      return new Subject<ModalResult>().asObservable();
    }

    return modal.resultSubject.asObservable();
  }

}